import { useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { hasNoSignal, markNoSignal, markSignal } from '../utils/connectivity';

// A dead connection (a bar of signal but no data) leaves requests hanging rather than failing,
// so pages would spin forever instead of falling back to saved gig data. Reads give up after a
// few seconds; uploads and saves keep the default (no timeout) so slow ones aren't cut off.
export const READ_TIMEOUT_MS = 6000;

const isRead = config => (config.method || 'get').toLowerCase() === 'get';
// The platform admin pages are never used at a gig, and some of their queries are slow on a
// cold start: a timeout there would flip the whole app into "no signal" mode, so the page's
// other requests would fail too.
const isAdmin = config => String(config.url || '').startsWith('/api/admin');

axios.interceptors.request.use(config => {
  if (!isRead(config) || isAdmin(config)) return config;
  // Known to have no signal: fail straight away so the page shows its saved copy
  if (hasNoSignal()) return Promise.reject(new AxiosError('No signal', AxiosError.ERR_NETWORK, config));
  if (!config.timeout) config.timeout = READ_TIMEOUT_MS;
  return config;
});

// Any answer from the server means there's signal; a request that never reached it means there isn't
axios.interceptors.response.use(
  res => { markSignal(); return res; },
  err => {
    if (err.response) markSignal();
    else if (!axios.isCancel(err) && err.message !== 'No signal' && !(err.config && isAdmin(err.config))) markNoSignal();
    return Promise.reject(err);
  }
);

// Cookies are same-origin (nginx proxies /api to the backend container),
// so Better Auth session cookies are sent automatically. No token header needed.
export default function AxiosInterceptor() {
  useEffect(() => {
    axios.defaults.withCredentials = true;
  }, []);
  return null;
}
